import { useMemo } from "react";

const SIGNS = ["Ari","Tau","Gem","Can","Leo","Vir","Lib","Sco","Sag","Cap","Aqu","Pis"];
const P_SHORT = { Sun:"Su", Moon:"Mo", Mars:"Ma", Mercury:"Me", Jupiter:"Ju", Venus:"Ve", Saturn:"Sa", Rahu:"Ra", Ketu:"Ke" };

const VARGA_ORDER = [
  ["D1",  "Rasi"],
  ["D2",  "Hora"],
  ["D3",  "Drekkana"],
  ["D7",  "Saptamsha"],
  ["D9",  "Navamsha"],
  ["D10", "Dashamsha"],
  ["D12", "Dwadashamsha"],
  ["D16", "Shodashamsha"],
  ["D20", "Vimshamsha"],
  ["D24", "Chaturvimshamsha"],
  ["D27", "Bhamsha"],
  ["D30", "Trimsamsha"],
  ["D40", "Khavedamsha"],
  ["D45", "Akshavedamsha"],
  ["D60", "Shashtiamsha"],
];

// South-Indian layout (row, col) — signs fixed on the frame
const SOUTH_LAYOUT = [
  { sign: 11, r: 0, c: 0 }, { sign: 0, r: 0, c: 1 }, { sign: 1, r: 0, c: 2 }, { sign: 2, r: 0, c: 3 },
  { sign: 10, r: 1, c: 0 }, { sign: 3, r: 1, c: 3 },
  { sign: 9,  r: 2, c: 0 }, { sign: 4, r: 2, c: 3 },
  { sign: 8,  r: 3, c: 0 }, { sign: 7, r: 3, c: 1 }, { sign: 6, r: 3, c: 2 }, { sign: 5, r: 3, c: 3 },
];

function SouthChartMini({ chart, ascSign, title, subtitle }) {
  const signPlanets = Array.from({ length: 12 }, () => []);
  Object.entries(chart || {}).forEach(([k, v]) => {
    if (k === "ASC") return;
    if (typeof v !== "number") return;
    signPlanets[v].push(P_SHORT[k] || k);
  });
  const cellAt = (r, c) => SOUTH_LAYOUT.find(x => x.r === r && x.c === c);

  return (
    <div className="border-2 border-[#B08A3E]/60 aspect-square relative bg-white/60 rounded-sm shadow-sm"
         data-testid={`allvarga-cell-${title}`}>
      <div className="absolute inset-0 grid grid-cols-4 grid-rows-4 gap-px p-px">
        {[0, 1, 2, 3].map((r) => [0, 1, 2, 3].map((c) => {
          const cell = cellAt(r, c);
          if (!cell) {
            const isCenter = (r === 1 || r === 2) && (c === 1 || c === 2);
            if (isCenter && r === 1 && c === 1) {
              return (
                <div key="center" className="col-span-2 row-span-2 flex items-center justify-center border border-[#B08A3E]/30 bg-[#FBF3DC]/70">
                  <div className="text-center px-1">
                    <div className="text-[10px] font-heading uppercase tracking-widest text-[#8B4513] font-bold leading-tight">{title}</div>
                    <div className="text-[9px] font-mono text-[#5B4A2A] mt-0.5 leading-tight">{subtitle}</div>
                  </div>
                </div>
              );
            }
            return null;
          }
          const isAsc = cell.sign === ascSign;
          return (
            <div key={`s-${cell.sign}`}
                 className="border p-0.5 text-[9px] bg-white/70 flex flex-col relative overflow-hidden"
                 style={{ borderColor: isAsc ? "#D4AF37" : "#B08A3E33" }}>
              {isAsc && (
                <div className="absolute top-0 left-0 w-3 h-3 pointer-events-none"
                     style={{ background: "linear-gradient(135deg, #D4AF37 0%, #D4AF37 50%, transparent 50%)" }} />
              )}
              <span className={`text-[8px] uppercase tracking-wider ${isAsc ? "text-[#8B4513] font-bold" : "text-[#8A857B]"}`}>
                {SIGNS[cell.sign]}
              </span>
              <div className="mt-auto flex flex-wrap gap-0.5">
                {signPlanets[cell.sign].map((p) => (
                  <span key={p}
                        className="text-[#3A2A0F] font-mono text-[8px] px-0.5 bg-[#FBF3DC] border border-[#B08A3E]/30 rounded-sm leading-none">
                    {p}
                  </span>
                ))}
              </div>
            </div>
          );
        }))}
      </div>
    </div>
  );
}

export default function AllVargasView({ chart }) {
  const rasiChart = useMemo(() => {
    if (!chart?.planets) return null;
    return Object.fromEntries(Object.entries(chart.planets).map(([k, v]) => [k, v.sign]));
  }, [chart]);

  if (!chart) return null;
  const rasiAsc = chart.ascendant?.asc_sign;

  const handlePrint = () => {
    document.documentElement.setAttribute("data-print-varga-sheet", "1");
    setTimeout(() => {
      window.print();
      // Restore soon after the print dialog closes
      setTimeout(() => document.documentElement.removeAttribute("data-print-varga-sheet"), 500);
    }, 50);
  };

  return (
    <section className="border-2 border-[#B08A3E] bg-gradient-to-br from-[#FBF3DC] to-[#F0E6C8] rounded-md p-5 sm:p-6 shadow-lg print-varga-sheet"
             data-testid="all-vargas-view">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4 pb-3 border-b border-[#B08A3E]/40 no-print">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] font-mono text-[#8B4513]">
            Full Horoscope · All Divisional Charts
          </div>
          <div className="font-heading text-xl sm:text-2xl text-[#3A2A0F] mt-0.5">
            D1 → D60 · Complete Varga Masterpiece
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-[10px] font-mono text-[#5B4A2A] max-w-xs text-right hidden md:block">
            15 divisional charts on one screen — read across, cross-check
            strengths at a glance.
          </div>
          <button
            onClick={handlePrint}
            data-testid="allvarga-print-btn"
            className="bg-gradient-to-r from-[#8B4513] to-[#B08A3E] text-white font-mono text-[10px] uppercase tracking-widest px-3 py-2 rounded-sm hover:brightness-110">
            Print Poster
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
        {VARGA_ORDER.map(([v, label]) => {
          const isD1 = v === "D1";
          const c = isD1 ? rasiChart : (chart.vargas || {})[v];
          const asc = isD1 ? rasiAsc : (chart.vargas || {})[v]?.ASC;
          if (!c) {
            return (
              <div key={v}
                   data-testid={`allvarga-empty-${v}`}
                   className="aspect-square border-2 border-dashed border-[#B08A3E]/40 rounded-sm flex items-center justify-center text-[10px] font-mono text-[#8A857B] bg-white/30">
                {v} unavailable
              </div>
            );
          }
          return <SouthChartMini key={v} chart={c} ascSign={asc} title={v} subtitle={label} />;
        })}
      </div>
    </section>
  );
}
